import clsx from 'clsx';
import React from 'react';
import FormHelperText from '../../atoms/FormHelperText/FormHelperText';
import FormValidationMessage from '../../atoms/FormValidationMessage/FormValidationMessage';
import useFormFieldAria from '../../hooks/useFormFieldAria';

/**
 * RadioGroup - Groups several InputRadio controls inside a <fieldset>/<legend>
 * with correct grouping semantics and automatic ARIA wiring (helper text, error).
 *
 * Sibling of FormField: FormField wraps a single input via <label htmlFor>, whereas
 * RadioGroup wraps multiple radios via <fieldset>/<legend>. The children are "naked"
 * InputRadio controls; the group owns the legend, helper text and error.
 *
 * Unlike CheckboxGroup, the radios must share a single `name` to be mutually
 * exclusive (and to enable native arrow-key navigation), so the group injects its
 * `name` into every child.
 */

type RadioGroupProps = {
  readonly children: React.ReactNode;
  readonly className?: string;
  readonly error?: boolean;
  readonly errorMessage?: string;
  readonly helperText?: string;
  readonly isDisabled?: boolean;
  /**
   * Visually hides the `<legend>` while keeping it in the DOM as the group's
   * accessible name. Use when the group is already labelled visually by a
   * surrounding element (e.g. an accordion tab title).
   */
  readonly isLegendHidden?: boolean;
  readonly isRequired?: boolean;
  readonly legend: string;
  /**
   * Shared `name` injected into every direct-child InputRadio (mutual exclusion +
   * arrow-key navigation). Optional: when the radios are wrapped in a layout element
   * the injection can't reach them, so omit `name` here and set it on each InputRadio
   * instead.
   */
  readonly name?: string;
};

function RadioGroup({
  children,
  className,
  error = false,
  errorMessage,
  helperText,
  isDisabled = false,
  isLegendHidden = false,
  isRequired = false,
  legend,
  name,
}: RadioGroupProps): JSX.Element {
  const { ariaDescribedBy, ids } = useFormFieldAria({ error, errorMessage, helperText });

  const shouldShowError = error && !!errorMessage;

  // Radios in a group must share one `name` to be mutually exclusive and to get
  // native arrow-key navigation. Inject the group's `name` into every child,
  // respecting a `name` a child already declares.
  const radios = React.Children.map(children, (child) =>
    React.isValidElement(child)
      ? React.cloneElement(child as React.ReactElement<{ name?: string }>, {
          name: (child.props as { name?: string }).name ?? name,
        })
      : child
  );

  return (
    <fieldset
      aria-describedby={ariaDescribedBy}
      // No `aria-required` here: it is not a valid ARIA attribute on a group/<fieldset>
      // (only on widget roles). `isRequired` is conveyed by the visual asterisk in the legend.
      className={clsx('radio-group', className, { 'radio-group--error': error })}
      // Native <fieldset disabled> disables every form control inside it, so all child
      // radios become :disabled. A child's own `disabled` still disables that single
      // option when the group as a whole is enabled.
      disabled={isDisabled}
    >
      <legend className={clsx('radio-group__legend', { 'sr-only': isLegendHidden })}>
        {legend} {isRequired && <sup aria-hidden="true">*</sup>}
      </legend>

      <div className="radio-group__options">{radios}</div>

      {helperText && <FormHelperText id={ids.helperId} text={helperText} />}

      {shouldShowError && <FormValidationMessage id={ids.validationId} text={errorMessage} type="error" />}
    </fieldset>
  );
}

export type { RadioGroupProps };
export default RadioGroup;
